import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchCategories,
  deleteCategory,
  updateCategoryField,
} from "../../Slice/Category/categorySlice";
import Swal from "sweetalert2";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { serverURL } from "../../services/FetchNodeServices";

const AllCategory = () => {
  const dispatch = useDispatch();
  const { categories, loading } = useSelector((state) => state.category);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Calculate pagination values
  const totalPages = Math.ceil(categories.length / itemsPerPage);

  const currentItems = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return categories.slice(start, start + itemsPerPage);
  }, [categories, currentPage]);

  useEffect(() => {
    dispatch(fetchCategories());
  }, [dispatch]);

  const handleDelete = (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "This action cannot be undone!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(deleteCategory(id))
          .then(() => {
            toast.success("Category deleted successfully");
          })
          .catch(() => toast.error("Failed to delete category"));
      }
    });
  };

  const handleToggle = (id, field, value) => {
    dispatch(updateCategoryField({ id, field, value: !value }))
      .then(() => {
        dispatch(fetchCategories());
        toast.success(`${field} updated successfully`);
      })
      .catch(() => toast.error(`Failed to update ${field}`));
  };

  const getImageUrl = (thumbnail) => {
    if (!thumbnail) return null;
    return thumbnail.startsWith("uploads/images")
      ? `${serverURL}/${thumbnail}`
      : `${serverURL}/uploads/images/${thumbnail}`;
  };

  return (
    <>
      <ToastContainer />
      <div className="bread">
        <div className="head">
          <h4>All Category List</h4>
        </div>
        <div className="links">
          <Link to="/add-category" className="add-new">
            Add New <i className="fa-solid fa-plus"></i>
          </Link>
        </div>
      </div>

      <section className="d-table">
        {loading ? (
          <p className="text-center my-4">Loading categories...</p>
        ) : categories.length === 0 ? (
          <p className="text-center my-4">No categories found.</p>
        ) : (
          <>
            <table className="table table-bordered table-striped table-hover">
              <thead>
                <tr>
                  <th>Sr.No.</th>
                  <th>Category ID</th>
                  <th>Name</th>
                  <th>Image</th>
                  <th>Status</th>
                  <th>Top</th>
                  <th>Popular</th>
                  <th>Created</th>
                  <th>Updated</th>
                  <th>Edit</th>
                  <th>Delete</th>
                </tr>
              </thead>
              <tbody>
                {currentItems.map((item, index) => (
                  <tr key={item.id}>
                    <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                    <td>{item.id}</td>
                    <td>{item.name}</td>
                    <td>
                      {item.thumbnail ? (
                        <img
                          src={getImageUrl(item.thumbnail)}
                          alt={item.name}
                          style={{
                            height: 60,
                            width: 60,
                            objectFit: "contain",
                          }}
                        />
                      ) : (
                        "N/A"
                      )}
                    </td>
                    <td>
                      <button
                        className={`btn btn-sm ${item.status ? "btn-success" : "btn-danger"
                          }`}
                        onClick={() =>
                          handleToggle(item.id, "status", item.status)
                        }
                      >
                        {item.status ? "Active" : "Inactive"}
                      </button>
                    </td>
                    <td>
                      <button
                        className={`btn btn-sm ${item.top ? "btn-success" : "btn-danger"
                          }`}
                        onClick={() =>
                          handleToggle(item.id, "top", item.top)
                        }
                      >
                        {item.top ? "Yes" : "No"}
                      </button>
                    </td>
                    <td>
                      <button
                        className={`btn btn-sm ${item.popular_category ? "btn-success" : "btn-danger"
                          }`}
                        onClick={() =>
                          handleToggle(
                            item.id,
                            "popular_category",
                            item.popular_category
                          )
                        }
                      >
                        {item.popular_category ? "Yes" : "No"}
                      </button>
                    </td>
                    <td>{item.create_date}</td>
                    <td>{item.modify_date}</td>
                    <td>
                      <Link
                        className="btn btn-warning btn-sm"
                        to={`/edit-category/${item.id}`}
                      >
                        Edit
                      </Link>
                    </td>
                    <td>
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => handleDelete(item.id)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <Pagination
              totalPages={totalPages}
              currentPage={currentPage}
              onPageChange={setCurrentPage}
            />
          </>
        )}
      </section>
    </>
  );
};

const Pagination = ({ totalPages, currentPage, onPageChange }) => {
  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxShown = 5;
    let start = Math.max(1, currentPage - Math.floor(maxShown / 2));
    let end = Math.min(totalPages, start + maxShown - 1);

    if (end - start < maxShown - 1) {
      start = Math.max(1, end - maxShown + 1);
    }

    for (let i = start; i <= end; i++) {
      pageNumbers.push(i);
    }
    return pageNumbers;
  };

  return (
    <nav>
      <ul className="pagination justify-content-center">
        <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
          <button
            onClick={() => onPageChange(currentPage - 1)}
            className="page-link"
          >
            Previous
          </button>
        </li>
        {getPageNumbers().map((number) => (
          <li
            key={number}
            className={`page-item ${currentPage === number ? "active" : ""}`}
          >
            <button
              onClick={() => onPageChange(number)}
              className="page-link"
            >
              {number}
            </button>
          </li>
        ))}
        <li
          className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}
        >
          <button
            onClick={() => onPageChange(currentPage + 1)}
            className="page-link"
          >
            Next
          </button>
        </li>
      </ul>
    </nav>
  );
};

export default AllCategory;
